import { useCallback, useMemo, useState, useEffect } from "react";

export type ClientPaginationSort = {
  key: string;
  dir: "asc" | "desc";
  compare: (a: any, b: any) => number;
};

type Params = {
  data: Array<any> | undefined;
  page?: number;
  perPage?: number;
  onFilter?: (arr: Array<any>) => Array<any>;
  sort?: ClientPaginationSort | undefined;
};

type Response = {
  totalItems: number;
  dataSlice: Array<any>;
  currentPage: number;
  totalFilteredItems: number;
  onChangePage: (p: number) => void;
};

export function useClientPagination(params: Params): Response {
  const { data, perPage = 10, onFilter, sort } = params;

  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!onFilter) return data;
    return onFilter(data);
  }, [data, onFilter]);

  const ordered = useMemo(() => {
    if (!sort?.compare) return filtered;
    return [...filtered].sort(sort.compare);
  }, [filtered, sort]);

  useEffect(() => {
    if (data?.length) {
      setPage(1);
    }
  }, [data?.length]);

  useEffect(() => {
    setPage(1);
  }, [sort?.key, sort?.dir]);

  const dataSlice = useMemo(() => {
    if (!ordered) return [];
    return ordered.slice((page - 1) * perPage, page * perPage);
  }, [ordered, page, perPage]);

  const handleChangePage = useCallback((p) => {
    setPage(p);
  }, []);

  return {
    dataSlice,
    totalItems: data?.length ?? 0,
    currentPage: page,
    totalFilteredItems: filtered?.length ?? 0,
    onChangePage: handleChangePage,
  };
}
